/** @jsxImportSource ../../i18n */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Zap } from 'lucide-react';
import api from '../../services/api';
import { formatDateTime, formatPercent } from '../../theme/format';
import { Badge, Card, CardHeader, SymbolMark } from '../ui';

// One explained move: the line, its change since the previous close, the
// cause found in the day's headlines (or the headlines themselves).
export const MoveRow = ({ move: m, showSymbol = true }) => (
  <li className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" data-testid="move-row">
    {showSymbol && <SymbolMark symbol={m.symbol} size="sm" className="mt-0.5" />}
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {showSymbol && <Link to={`/stock/${encodeURIComponent(m.symbol)}`} className="truncate text-sm font-medium text-fg hover:text-gold-300">{m.symbol}</Link>}
        <Badge tone={m.change_pct < 0 ? 'loss' : 'gain'}>{formatPercent(m.change_pct)}</Badge>
        <span className="text-xs text-fg-subtle">{formatDateTime(m.created_at)}</span>
      </div>
      {m.explanation ? (
        <p className="mt-1 text-sm leading-snug text-fg-body">
          {m.explanation}
          {m.source && (
            <>
              {' '}
              {m.url ? (
                <a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gold-300 hover:underline">
                  {m.source}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              ) : <span className="text-xs text-fg-subtle">{m.source}</span>}
            </>
          )}
        </p>
      ) : (
        <div className="mt-1 text-sm text-fg-muted">
          <p>Pas d’explication dans l’actualité du jour.</p>
          {(m.headlines || []).slice(0, 2).map((h) => (
            <p key={h.url || h.title} className="mt-0.5 truncate text-xs">
              {h.url ? <a href={h.url} target="_blank" rel="noopener noreferrer" className="hover:text-gold-300">{h.title}</a> : h.title}
              {h.source && <span className="text-fg-subtle"> · {h.source}</span>}
            </p>
          ))}
        </div>
      )}
    </div>
  </li>
);

// "Pourquoi ça bouge ?": the held lines' sharp moves of the last days, each
// with its cause from the day's news. Hidden when there is none.
export const MovesCard = ({ className, symbol }) => {
  const [moves, setMoves] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/portfolio/moves', symbol ? { params: { symbol } } : undefined)
      .then((res) => alive && setMoves(res.data?.moves || []))
      .catch(() => alive && setMoves([]));
    return () => { alive = false; };
  }, [symbol]);

  if (!moves || moves.length === 0) return null;
  return (
    <Card className={className} data-testid="moves-card">
      <CardHeader
        icon={Zap}
        title="Pourquoi ça bouge ?"
        description={symbol
          ? 'Les fortes variations de cette ligne ces derniers jours, et leur cause dans l’actualité.'
          : 'Les fortes variations de vos lignes en séance, expliquées par l’actualité du jour. Le seuil se règle dans Réglages › Notifications.'}
      />
      <ul className="divide-y divide-line">
        {moves.slice(0, 6).map((m) => <MoveRow key={m.id} move={m} showSymbol={!symbol} />)}
      </ul>
    </Card>
  );
};

export default MovesCard;
